import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  settingsPublicAccessCredentialsCreateMutation,
  settingsPublicAccessCredentialsListOptions,
  settingsPublicAccessCredentialsListQueryKey,
  settingsPublicAccessCredentialsPartialUpdateMutation,
  settingsPublicAccessCredentialsRetrieveOptions,
  settingsPublicAccessCredentialsRetrieveQueryKey,
  settingsPublicAccessCredentialsRevokeCreateMutation,
  settingsPublicAccessCredentialsRotateCreateMutation
} from 'src/api/main'
import { useShowApiErrors } from 'src/utils'

/**
 * Mutation-only slice for public-access credentials. Both the list page and
 * the detail page need create/update/revoke/rotate, but only the list page
 * needs the list query — sharing this keeps mutations DRY without forcing the
 * detail page to also hydrate the whole list.
 */
export const useWorkspacePublicAccessCredentialActions = (
  workspaceId: number
) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()

  const listQueryKey = settingsPublicAccessCredentialsListQueryKey({
    path: { organization_id: workspaceId }
  })

  const invalidateAll = (credentialId?: string) => {
    void queryClient.invalidateQueries({ queryKey: listQueryKey })
    if (credentialId) {
      void queryClient.invalidateQueries({
        queryKey: settingsPublicAccessCredentialsRetrieveQueryKey({
          path: { id: credentialId, organization_id: workspaceId }
        })
      })
    }
  }

  const { mutateAsync: createCredential, isPending: isCreating } = useMutation({
    ...settingsPublicAccessCredentialsCreateMutation(),
    onSuccess: () => invalidateAll(),
    onError: (err) => showApiErrors(err)
  })

  const { mutateAsync: updateCredential, isPending: isUpdating } = useMutation({
    ...settingsPublicAccessCredentialsPartialUpdateMutation(),
    onSuccess: (_data, variables) => {
      message.success(t('workspace.publicAccess.messages.updated'))
      invalidateAll(variables.path?.id)
    },
    onError: (err) => showApiErrors(err)
  })

  const { mutateAsync: revokeCredential, isPending: isRevoking } = useMutation({
    ...settingsPublicAccessCredentialsRevokeCreateMutation(),
    onSuccess: (_data, variables) => {
      message.success(t('workspace.publicAccess.messages.revoked'))
      invalidateAll(variables.path?.id)
    },
    onError: (err) => showApiErrors(err)
  })

  const { mutateAsync: rotateCredential, isPending: isRotating } = useMutation({
    ...settingsPublicAccessCredentialsRotateCreateMutation(),
    onSuccess: (_data, variables) => invalidateAll(variables.path?.id),
    onError: (err) => showApiErrors(err)
  })

  return {
    createCredential,
    isCreating,
    updateCredential,
    isUpdating,
    revokeCredential,
    isRevoking,
    rotateCredential,
    isRotating
  }
}

/**
 * Wraps the org-scoped public-access credential endpoints
 * (`/settings/public-access/{organization_id}/credentials/`). The list is a
 * plain array (no pagination), so sorting/paging is handled client-side in the
 * table.
 */
export const useWorkspacePublicAccessCredentials = (workspaceId: number) => {
  const listEnabled = Number.isFinite(workspaceId) && workspaceId > 0
  const listOptions = { path: { organization_id: workspaceId } }

  const {
    data: credentials = [],
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...settingsPublicAccessCredentialsListOptions(listOptions),
    enabled: listEnabled,
    // Opt out of the app-wide refetchOnMount:false so returns show fresh rows.
    refetchOnMount: 'always'
  })

  const actions = useWorkspacePublicAccessCredentialActions(workspaceId)

  return {
    credentials,
    isLoading,
    isError,
    refetch,
    ...actions
  }
}

/**
 * Fetches a single credential by id. Used on the detail page — the list
 * endpoint returns the same shape, but retrieving directly keeps the detail
 * page loadable via URL without hydrating the whole list first.
 */
export const useWorkspacePublicAccessCredential = (
  workspaceId: number,
  credentialId: string
) => {
  const enabled =
    Number.isFinite(workspaceId) && workspaceId > 0 && !!credentialId

  return useQuery({
    ...settingsPublicAccessCredentialsRetrieveOptions({
      path: { id: credentialId, organization_id: workspaceId }
    }),
    enabled
  })
}
